import Parser from 'rss-parser';
import type { FeedItem } from '../schemas/feed-item';

const parser = new Parser();

/**
 * Validates and normalises an RSS/Atom feed URL.
 * Returns the normalised URL string or throws if invalid.
 */
export function parseFeedUrl(raw: string): string {
  const url = new URL(raw);
  if (!['http:', 'https:'].includes(url.protocol)) {
    throw new Error(`Unsupported protocol: ${url.protocol}`);
  }
  return url.toString();
}

export interface ParsedFeedResult {
  title: string | undefined;
  description: string | undefined;
  items: Array<{
    guid: string;
    title: string;
    url: string;
    content: string | null;
    author: string | null;
    published_at: string | null;
  }>;
}

/**
 * Fetches and parses an RSS/Atom feed from a URL.
 * Returns structured feed data with normalised fields.
 */
export async function fetchAndParseFeed(feedUrl: string): Promise<ParsedFeedResult> {
  const url = parseFeedUrl(feedUrl);
  const feed = await parser.parseURL(url);

  return {
    title: feed.title,
    description: feed.description,
    items: (feed.items ?? []).map((item) => ({
      guid: item.guid ?? item.link ?? item.title ?? '',
      title: item.title ?? '',
      url: item.link ?? '',
      content: item.content ?? item.contentSnippet ?? null,
      author: item['creator'] ?? item['author'] ?? null,
      published_at: item.isoDate ?? null,
    })),
  };
}

/**
 * Generates a valid RSS 2.0 XML string from an array of FeedItems.
 */
export function generateRssXml(
  items: FeedItem[],
  channelTitle: string,
  channelLink: string,
  channelDescription?: string,
): string {
  const escapeXml = (str: string): string =>
    str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');

  const itemsXml = items
    .map((item) => {
      const parts = [
        `      <title>${escapeXml(item.title)}</title>`,
        `      <link>${escapeXml(item.url)}</link>`,
        `      <guid>${escapeXml(item.guid)}</guid>`,
      ];
      if (item.content) {
        parts.push(`      <description>${escapeXml(item.content)}</description>`);
      }
      if (item.author) {
        parts.push(`      <author>${escapeXml(item.author)}</author>`);
      }
      if (item.published_at) {
        parts.push(`      <pubDate>${new Date(item.published_at).toUTCString()}</pubDate>`);
      }
      return `    <item>\n${parts.join('\n')}\n    </item>`;
    })
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<rss version="2.0">
  <channel>
    <title>${escapeXml(channelTitle)}</title>
    <link>${escapeXml(channelLink)}</link>
    <description>${escapeXml(channelDescription ?? '')}</description>
    <lastBuildDate>${new Date().toUTCString()}</lastBuildDate>
${itemsXml}
  </channel>
</rss>`;
}
